import APTable, {
  APHeaderRow,
  APTableProps,
} from "../../../../components/APTable";
import { toDollars } from "../../../../helpers/currencies";
import {
  DepreciationCalculationResult,
  YearlyCalculation,
} from "../../../../types/Calculation";
import { depreciationMethodLabels } from "../../../../types/Quote";

type TableDataDefinition = {
  year: string;
  periodStart: string;
  periodEnd: string;
  days: string;
  startingAdjustableValue: string;
  depreciationMethod: string;
  depreciation: string;
  endAdjustableValue: string;
};

const yearlyCaculationToTableRow = (
  year: YearlyCalculation<DepreciationCalculationResult>
): Record<keyof TableDataDefinition, string> => {
  const {
    startingAdjustableValue,
    depreciationMethod,
    depreciation,
    endAdjustableValue,
  } = year.calculation.result;

  return {
    year: `${year.yearIndex + 1}`,
    periodStart: year.startDate,
    periodEnd: year.endDate,
    days: `${year.daysHeld}`,
    startingAdjustableValue: toDollars(startingAdjustableValue, 2),
    depreciationMethod: depreciationMethodLabels[depreciationMethod],
    depreciation: toDollars(depreciation.result, 2),
    endAdjustableValue: toDollars(endAdjustableValue.result, 2),
  };
};

const yearlyCaculationToTableHeaderRow = (
  year: YearlyCalculation<DepreciationCalculationResult>
): Record<keyof TableDataDefinition, APHeaderRow> => {
  const { depreciation, endAdjustableValue } = year.calculation.result;

  return {
    year: { title: "Year" },
    periodStart: { title: "Period Start" },
    periodEnd: { title: "Period End" },
    days: { title: "Days" },
    startingAdjustableValue: {
      title: "Starting Adjustable Value",
    },
    depreciationMethod: {
      title: "Method",
    },
    depreciation: {
      title: depreciation.name,
      titleTooltip: depreciation.formula,
    },
    endAdjustableValue: {
      title: endAdjustableValue.name,
      titleTooltip: endAdjustableValue.formula,
    },
  };
};

const DepreciationTable = ({
  calculations,
  totalDepreciation,
  endAdjustableValue,
}: {
  calculations: YearlyCalculation<DepreciationCalculationResult>[];
  totalDepreciation: number;
  endAdjustableValue: number;
}) => {
  const rows = calculations.map((calc) => yearlyCaculationToTableRow(calc));

  const tableProps: APTableProps<TableDataDefinition> = {
    columns: [
      "year",
      "periodStart",
      "periodEnd",
      "days",
      "startingAdjustableValue",
      "depreciationMethod",
      "depreciation",
      "endAdjustableValue",
    ],
    headerRows:
      calculations.length > 0
        ? yearlyCaculationToTableHeaderRow(calculations[0])
        : {},
    rows: rows,
    totalRow: {
      year: "Total",
      depreciation: toDollars(totalDepreciation, 2),
      endAdjustableValue: toDollars(endAdjustableValue, 2),
    },
    showTotalRow: true,
  };

  return <APTable {...tableProps} />;
};

export default DepreciationTable;
